import { Badge, Button, Card, DataTable, Icon, useToast, type Column } from "@nabble/ui";
import { useCallback, useMemo, useState } from "react";
import { closeSession, type AccountSession } from "../../../../api/accountSessions";
import { errorMessage } from "../../../../errors";

/** My account → Sessions: where the person is signed in. */
export function SessionsSection({ sessions, onChanged }: { sessions: AccountSession[]; onChanged: () => void }): React.JSX.Element {
  const { show } = useToast();
  const [closingOthers, setClosingOthers] = useState(false);
  const others = sessions.filter((s) => !s.current);

  const close = useCallback(
    async (handle: string): Promise<void> => {
      try {
        await closeSession(handle);
        show({ title: "Session closed", tone: "success" });
        onChanged();
      } catch (err) {
        show({ title: "We couldn't close the session", text: errorMessage(err, "Please try again."), tone: "danger" });
      }
    },
    [show, onChanged],
  );

  const columns = useMemo<Column<AccountSession>[]>(
    () => [
      {
        key: "handle",
        label: "Session",
        minWidth: 200,
        render: (s) => (
          <div className="nb-cell-main">
            <span className="nb-link-card-icon" style={{ width: 28, height: 28 }}>
              <Icon name="monitor" />
            </span>
            <div className="nb-cell-stack">
              <strong>
                {s.current ? "This browser" : "Another session"} {s.current ? <Badge label="Current" tone="success" /> : null}
              </strong>
              <span className="nb-cell-sub nb-mono" title={s.handle}>
                {s.handle.slice(0, 12)}
              </span>
            </div>
          </div>
        ),
      },
      { key: "createdAt", label: "Started", type: "datetime", width: 150 },
      { key: "lastAccessedAt", label: "Last activity", type: "datetime", width: 170 },
      {
        key: "actions",
        label: "Actions",
        type: "actions",
        width: 56,
        actions: (s) => [{ label: "Close this session", icon: "logout", inline: true, hidden: s.current, onSelect: () => void close(s.handle) }],
      },
    ],
    [close],
  );

  return (
    <Card id="sessions" className="nb-setting" padded={false}>
      <div className="nb-setting-row is-stacked">
        <div className="nb-setting-head">
          <div>
            <div className="nb-setting-title">Open sessions</div>
            <p className="nb-setting-desc">If you don't recognize a session, close it and change your password.</p>
          </div>
        </div>
        <div className="nb-setting-content">
          <DataTable<AccountSession>
            minimal
            rowKey="handle"
            rows={sessions}
            columns={columns}
            defaultSort={{ key: "lastAccessedAt", dir: "desc" }}
            ariaLabel="Open sessions"
          />
          {others.length > 0 ? (
            <div>
              <Button
                label={others.length === 1 ? "Close the other session" : `Close the other ${others.length} sessions`}
                variant="secondary"
                size="sm"
                icon="logout"
                loading={closingOthers}
                onClick={() => {
                  setClosingOthers(true);
                  void Promise.allSettled(others.map((s) => closeSession(s.handle)))
                    .then(() => {
                      show({ title: "We closed your other sessions", tone: "success" });
                      onChanged();
                    })
                    .finally(() => setClosingOthers(false));
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
